/**
 * useClipboard Hook
 * Handles copy (Ctrl+C) and paste (Ctrl+V) for selected map editor elements.
 * Uses internal memory clipboard (useRef), no system clipboard.
 */

import { useCallback, useRef } from 'react'
import { dia, shapes } from '@joint/core'
import { ElementId } from '../../../map-editor-shared/types/branded'
import { generateObjectName } from '../../../map-editor-shared/lib/objectNameGenerator'
import { useObjectTypeStore } from '../../../map-editor-shared/store/objectTypeStore'
import { useEditorStore } from '../../../map-editor-shared/store/editorStore'

interface SerializedElement {
  json: Record<string, any>
  typeId: string | null
  isGroup: boolean
}

interface ClipboardData {
  elements: SerializedElement[]
  sourceOrigin: { x: number; y: number }
}

interface UseClipboardReturn {
  handleCopy: () => void
  handlePaste: () => void
}

const PASTE_OFFSET = 20

export function useClipboard(
  graph: dia.Graph | null,
  selectedElementIds: string[],
  currentFloor: string | null,
  setElementCount: (count: number) => void
): UseClipboardReturn {
  const clipboardRef = useRef<ClipboardData | null>(null)
  const pasteCountRef = useRef(0)

  const storeSetSelectedElementIds = useEditorStore((state) => state.setSelectedElementIds)
  const getLayerTypeMappingsByFloorId = useObjectTypeStore(
    (state) => state.getLayerTypeMappingsByFloorId
  )

  const handleCopy = useCallback(() => {
    if (!graph || selectedElementIds.length === 0) return

    const elements = selectedElementIds
      .map((id) => graph.getCell(id))
      .filter((cell): cell is dia.Element => !!cell?.isElement())

    if (elements.length === 0) return

    // Calculate bounding box origin for relative positioning
    let minX = Infinity
    let minY = Infinity
    for (const el of elements) {
      const pos = el.position()
      if (pos.x < minX) minX = pos.x
      if (pos.y < minY) minY = pos.y
    }

    const serialized: SerializedElement[] = elements.map((el) => ({
      json: JSON.parse(JSON.stringify(el.toJSON())),
      typeId: el.get('data')?.typeId || null,
      isGroup: el.get('data')?.isGroup === true,
    }))

    clipboardRef.current = {
      elements: serialized,
      sourceOrigin: { x: minX, y: minY },
    }
    pasteCountRef.current = 0
  }, [graph, selectedElementIds])

  const handlePaste = useCallback(() => {
    if (!graph || !clipboardRef.current || clipboardRef.current.elements.length === 0) return

    const offset = PASTE_OFFSET * (pasteCountRef.current + 1)
    const mappings = getLayerTypeMappingsByFloorId(currentFloor || '')
    const newIds: string[] = []

    graph.startBatch('paste')

    for (const serialized of clipboardRef.current.elements) {
      const clone: Record<string, any> = JSON.parse(JSON.stringify(serialized.json))

      // Remove id so JointJS assigns a new one
      delete clone.id
      // 부모 그룹 참조 stale 방지 — 그룹 cell 자체는 별도 children 리스트 관리.
      // 복사된 새 객체는 paper 좌표 동일 layer/groupName(data.*) 로만 묶이고,
      // 필요 시 사용자가 다시 그룹화하도록 둠. (사용자 요구: "동일한 그룹에 새 객체 추가" —
      // data.layer / data.groupName 보존만으로 충분.)
      delete clone.parent

      // Apply position offset
      if (clone.position) {
        clone.position.x += offset
        clone.position.y += offset
      }

      // Regenerate name
      if (serialized.typeId && clone.data?.properties) {
        clone.data.properties.name = generateObjectName(
          serialized.typeId,
          graph,
          mappings
        )
      } else if (clone.data?.properties?.name) {
        clone.data.properties.name = `${clone.data.properties.name} copy`
      }

      // Handle group: offset originalElements positions
      if (serialized.isGroup && clone.data?.originalElements) {
        for (const orig of clone.data.originalElements) {
          if (orig.position) {
            orig.position.x += offset
            orig.position.y += offset
          }
        }
      }

      // Create element from clone data and add to graph
      // Use the type from serialized JSON to reconstruct the correct shape
      const cellType = clone.type || 'standard.Rectangle'
      let newElement: dia.Element

      if (cellType === 'standard.Image') {
        newElement = new shapes.standard.Image(clone)
      } else if (cellType === 'standard.Polygon') {
        newElement = new shapes.standard.Polygon(clone)
      } else {
        newElement = new shapes.standard.Rectangle(clone)
      }

      graph.addCell(newElement)
      newIds.push(newElement.id as string)
    }

    graph.stopBatch('paste')

    // Select newly pasted elements
    storeSetSelectedElementIds(newIds.map((id) => id as ElementId))
    setElementCount(graph.getElements().length)
    pasteCountRef.current++
  }, [graph, currentFloor, getLayerTypeMappingsByFloorId, storeSetSelectedElementIds, setElementCount])

  return { handleCopy, handlePaste }
}
